/**
 * Swatch filter component for color/material facets.
 *
 * Displays color swatches instead of checkboxes for visual filtering.
 * Supports case materials (with SVG icons), dial colors, and strap colors.
 * Brand-themed with CSS custom properties.
 * Preserves original ordering of options when selections are made.
 */

'use client';

import { CheckIcon } from '@/components/ui/CheckIcon';
import { Icon } from '@/components/ui/Icon';
import { CASE_SVG_MAPPING, getCaseSvg, getColorMapping } from '@/lib/algolia/colorMappings';
import { extractFacetId, extractFacetLabel, facetValueMatchesId } from '@/lib/algolia/facetValues';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import Image from 'next/image';
import { useMemo, useRef, useState } from 'react';
import styles from './SwatchFilter.module.scss';

interface SwatchFilterProps {
  /** Facet name (e.g., 'case', 'dial_color', 'color') */
  name: string;
  /** Display name for the filter header */
  displayName: string;
  /** Facet values with counts: [fullValue, count][] */
  values: [string, number][];
  /** Currently active filter IDs from URL params */
  activeIds: string[];
}

/**
 * All supported swatch filter parameter names.
 * These map to URL parameters via useSearchParams.
 */
type SwatchParamName =
  // Watch filters
  | 'case'
  | 'dial-color'
  | 'bracelet-color'
  // Strap filters
  | 'color'
  // Eyewear filters
  | 'lens-color';

/**
 * Map base facet names to URL parameter names.
 * Swatch filters display color/material swatches instead of checkboxes.
 */
const facetToParam: Record<string, SwatchParamName> = {
  // Watch facets
  case: 'case',
  dial_color: 'dial-color',
  bracelet_category_color: 'bracelet-color',
  // Strap facets
  color: 'color',
  // Eyewear facets
  lens_color: 'lens-color',
};

export function SwatchFilter({ name, displayName, values, activeIds }: SwatchFilterProps) {
  const [isExpanded, setIsExpanded] = useState(activeIds.length > 0);
  const { toggleFilterValue } = useSearchParams();

  // Track original order of facet values to prevent reordering on selection
  const originalOrderRef = useRef<string[] | null>(null);

  const paramName = facetToParam[name];
  const colorMapping = getColorMapping(
    name as
      | 'case'
      | 'dial_color'
      | 'bracelet_category_color'
      | 'color'
      | 'frame_color'
      | 'lens_color'
  );

  // Parse values to extract IDs, labels, and check active state
  const parsedValues = useMemo(() => {
    const parsed = values.map(([fullValue, count]) => {
      const id = extractFacetId(fullValue, name);
      const label = extractFacetLabel(fullValue, name);
      const isActive = activeIds.some((activeId) => facetValueMatchesId(fullValue, activeId, name));

      // Get color/gradient for this ID
      const color = colorMapping[id];

      // Check if this is a case material with an SVG icon
      const svgIcon = name === 'case' ? getCaseSvg(id) : undefined;

      // Check if this is a gem-set material (special handling)
      const isGemSet = name === 'case' && fullValue.toLowerCase().includes('gem-set');

      return {
        fullValue,
        id,
        label,
        count,
        isActive,
        color,
        svgIcon,
        isGemSet,
      };
    });

    // Capture original order on first render (when no active filters)
    if (originalOrderRef.current === null && parsed.length > 0) {
      originalOrderRef.current = parsed.map((p) => p.id);
    }

    // Sort by original order to prevent reordering when selections change
    if (originalOrderRef.current) {
      const orderMap = new Map(originalOrderRef.current.map((id, index) => [id, index]));
      parsed.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? Infinity;
        const orderB = orderMap.get(b.id) ?? Infinity;
        return orderA - orderB;
      });
    }

    return parsed;
  }, [values, name, activeIds, colorMapping]);

  // Early return if no valid param name
  if (!paramName) return null;

  const handleToggle = (id: string) => {
    toggleFilterValue(paramName, id);
  };

  const handleKeyDown = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle(id);
    }
  };

  return (
    <div className={`${styles.facet} ${isExpanded ? styles.expanded : ''}`}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.name}>
          {displayName}
          {activeIds.length > 0 && <span className={styles.activeCount}>{activeIds.length}</span>}
        </span>
        <Icon
          name="chevron-down"
          size={20}
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
        />
      </button>

      {isExpanded && (
        <div className={styles.swatchGrid}>
          {parsedValues.map(({ id, label, count, isActive, color, svgIcon, isGemSet }) => (
            <div
              key={id}
              className={`${styles.swatchOption} ${isActive ? styles.active : ''}`}
              onClick={() => handleToggle(id)}
              onKeyDown={(e) => handleKeyDown(e, id)}
              tabIndex={0}
              role="checkbox"
              aria-checked={isActive}
              aria-label={`${label} (${count} items)`}
            >
              <div className={styles.swatchWrapper}>
                {/* SVG icon for special materials */}
                {svgIcon && (
                  <div className={styles.svgSwatch}>
                    <Image
                      src={svgIcon}
                      alt={label}
                      width={24}
                      height={24}
                      className={styles.svgIcon}
                    />
                  </div>
                )}

                {/* Gem-set with PNG image */}
                {isGemSet && !CASE_SVG_MAPPING[id] && (
                  <div className={styles.svgSwatch}>
                    <Image
                      src="/assets/cases/gem-set.png"
                      alt="Gem-set"
                      width={24}
                      height={24}
                      className={styles.svgIcon}
                    />
                  </div>
                )}

                {/* Color swatch for regular colors */}
                {!svgIcon && !isGemSet && (
                  <div
                    className={`${styles.colorSwatch} ${isActive ? styles.active : ''}`}
                    style={{ background: color || 'silver' }}
                  />
                )}

                {/* Active indicator */}
                {isActive && (
                  <div className={styles.activeIndicator}>
                    <CheckIcon size={12} />
                  </div>
                )}
              </div>

              <span className={styles.labelRow}>
                <span className={`${styles.label} ${isActive ? styles.active : ''}`}>{label}</span>{' '}
                <span className={styles.count}>({count})</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
